import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  BanknotesIcon,
  InformationCircleIcon,
  ArrowRightIcon,
  CalculatorIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

interface AdvanceDrawProps {
  selectedCommitment: any;
  eligibilityResults: any;
  onUpdateState: (updates: any) => void;
}

const AdvanceDraw: React.FC<AdvanceDrawProps> = ({ 
  selectedCommitment, 
  eligibilityResults, 
  onUpdateState 
}) => {
  const navigate = useNavigate();
  const [includeAdvance, setIncludeAdvance] = useState(false);
  const [advanceAmount, setAdvanceAmount] = useState(0);
  const [customAdvance, setCustomAdvance] = useState('');

  const maxAdvance = selectedCommitment?.commitmentAmount * 0.01 || 0; // 1% max
  const sharePrice = eligibilityResults?.data?.stockData?.price || eligibilityResults?.price || 1; // Use actual stock price with fallback

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('en-US').format(Math.round(num));
  };

  const handleAdvanceChange = (value: number) => {
    setAdvanceAmount(Math.min(value, maxAdvance));
    setCustomAdvance('');
  };

  const handleCustomAdvanceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^0-9]/g, '');
    setCustomAdvance(value);
    if (value) {
      setAdvanceAmount(Math.min(parseInt(value), maxAdvance));
    }
  };

  const calculateCommitmentShares = () => {
    if (!includeAdvance || advanceAmount === 0) return 0;
    // Commitment shares = (Advance Draw / 2) / Share Price
    return Math.floor((advanceAmount / 2) / sharePrice);
  };

  const calculateNetProceeds = () => {
    // 10% OID - user receives 90% of advance draw
    return advanceAmount * 0.9;
  };

  const handleProceed = () => {
    // Debug logging
    console.log('=== ADVANCE DRAW HANDLE PROCEED ===');
    console.log('eligibilityResults:', eligibilityResults);
    console.log('selectedCommitment:', selectedCommitment);
    
    const eligibilityCheckId = eligibilityResults.facilityId || eligibilityResults.data?.eligibilityId || eligibilityResults._id;
    console.log('Determined eligibilityCheckId:', eligibilityCheckId);
    
    const finalSelection = {
      ...selectedCommitment,
      includeAdvance,
      advanceAmount: includeAdvance ? advanceAmount : 0,
      netProceeds: includeAdvance ? calculateNetProceeds() : 0,
      commitmentShares: calculateCommitmentShares(),
      ticker: eligibilityResults.ticker,
      companyName: eligibilityResults.companyName,
      sharePriceAtSelection: sharePrice,
      registrationDeadline: includeAdvance ? 20 : 30, // 20 days if advance, 30 otherwise
      eligibilityCheckId: eligibilityCheckId,
      vwapPercent: eligibilityResults.data?.underwriting?.vwapPercent,
      rapidPercent: eligibilityResults.data?.underwriting?.rapidPercent,
      registrationRequirement: includeAdvance ? '20 trading days' : '30 calendar days',
    };
    
    console.log('Final selection object:', finalSelection);
    
    onUpdateState({ finalSelection });
    navigate('/confirmation');
  };

  const advanceOptions = [
    { label: '0.25%', value: selectedCommitment?.commitmentAmount * 0.0025 },
    { label: '0.50%', value: selectedCommitment?.commitmentAmount * 0.005 },
    { label: '0.75%', value: selectedCommitment?.commitmentAmount * 0.0075 },
    { label: '1.00%', value: selectedCommitment?.commitmentAmount * 0.01 },
  ];

  return (
    <div className="min-h-screen bg-gradient-financial py-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-secondary-900 mb-4">
            Advance Draw Option
          </h1>
          <p className="text-xl text-secondary-600">
            Access immediate funding at closing (optional)
          </p>
        </div>

        {/* Selection Summary */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="card-elevated mb-8"
        >
          <h3 className="text-lg font-semibold text-secondary-900 mb-4">Your Selected Facility</h3>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="bg-secondary-50 rounded-lg p-4">
              <p className="text-sm text-secondary-600 mb-1">Facility Size</p>
              <p className="text-xl font-bold text-secondary-900">
                {formatCurrency(selectedCommitment?.commitmentAmount || 0)}
              </p>
            </div>
            <div className="bg-secondary-50 rounded-lg p-4">
              <p className="text-sm text-secondary-600 mb-1">Discount Rate</p>
              <p className="text-xl font-bold text-secondary-900">
                {selectedCommitment?.discountRate || 0}%
              </p>
            </div>
            <div className="bg-secondary-50 rounded-lg p-4">
              <p className="text-sm text-secondary-600 mb-1">Max Advance Draw</p>
              <p className="text-xl font-bold text-secondary-900">
                {formatCurrency(maxAdvance)}
              </p>
            </div>
          </div>
        </motion.div>

        {/* Advance Draw Toggle */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="card mb-8"
        >
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <BanknotesIcon className="h-6 w-6 text-primary-600" />
              <h3 className="text-xl font-semibold text-secondary-900">
                Include Advance Draw?
              </h3>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={includeAdvance}
                onChange={(e) => setIncludeAdvance(e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-secondary-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-950"></div>
            </label>
          </div>

          {includeAdvance && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              transition={{ duration: 0.3 }}
            >
              {/* Info Box */}
              <div className="bg-primary-50 border border-primary-200 rounded-xl p-4 mb-6">
                <div className="flex gap-3">
                  <InformationCircleIcon className="h-5 w-5 text-primary-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-primary-700">
                    <p className="font-medium mb-1">About Advance Draws</p>
                    <p className="mb-2">Access up to 1% of your facility immediately at closing with a 10% Original Issue Discount (OID).</p>
                    <ul className="space-y-1 text-xs">
                      <li>• You receive 90% of the advance amount (same business day)</li>
                      <li>• Settlement via Repayment Notices, at the discretion of the Investor, and priced using the lowest daily VWAP of the prior 10 trading days</li>
                      <li>• Registration statement (S-1/F-1) required within 20 business days</li>
                      <li>• Commitment shares issued: 50% of advance value at current price</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Amount Selection */}
              <div className="space-y-4">
                <p className="text-secondary-700 font-medium">Select advance amount:</p>
                
                {/* Quick Options */}
                <div className="grid grid-cols-3 gap-3">
                  {advanceOptions.map((option) => (
                    <button
                      key={option.label}
                      onClick={() => handleAdvanceChange(option.value)}
                      className={`p-4 rounded-xl border-2 transition-all ${
                        advanceAmount === option.value
                          ? 'border-primary-500 bg-primary-50'
                          : 'border-secondary-200 hover:border-secondary-300'
                      }`}
                    >
                      <p className="text-sm text-secondary-600">{option.label}</p>
                      <p className="font-semibold text-secondary-900">
                        {formatCurrency(option.value)}
                      </p>
                    </button>
                  ))}
                </div>

                {/* Custom Amount */}
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Or enter custom amount:
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-secondary-500">
                      $
                    </span>
                    <input
                      type="text"
                      value={customAdvance || (advanceAmount && !advanceOptions.some(opt => opt.value === advanceAmount) ? advanceAmount : '')}
                      onChange={handleCustomAdvanceChange}
                      placeholder="0"
                      className="w-full pl-8 pr-4 py-3 border border-secondary-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                    <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-sm text-secondary-500">
                      Max: {formatCurrency(maxAdvance)}
                    </span>
                  </div>
                </div>

                {/* Calculation Display */}
                {advanceAmount > 0 && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-success-50 border border-success-200 rounded-xl p-4 mt-4"
                  >
                    <div className="flex items-center gap-2 mb-3">
                      <CalculatorIcon className="h-5 w-5 text-success-600" />
                      <h4 className="font-semibold text-success-700">Advance Draw Details</h4>
                    </div>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-success-600">Advance Amount</p>
                        <p className="font-semibold text-success-900">{formatCurrency(advanceAmount)}</p>
                      </div>
                      <div>
                        <p className="text-success-600">Net Proceeds (90%)</p>
                        <p className="font-semibold text-success-900">
                          {formatCurrency(calculateNetProceeds())}
                        </p>
                      </div>
                      <div className="col-span-2">
                        <p className="text-success-600">Commitment Shares Issued</p>
                        <p className="font-semibold text-success-900">
                          {formatNumber(calculateCommitmentShares())} shares
                        </p>
                        <p className="text-xs text-success-600 mt-1">
                          (50% of advance value at current market price)
                        </p>
                      </div>
                    </div>
                    <div className="mt-3 pt-3 border-t border-success-200">
                      <p className="text-xs text-success-700">
                        <span className="font-medium">Note:</span> 10% OID applied. Settlement via Repayment Notices at the lowest daily VWAP of the prior 10 trading days.
                      </p>
                    </div>
                  </motion.div>
                )}
              </div>
              
              {/* Remove the entire registration requirement section */}
            </motion.div>
          )}
        </motion.div>

        {/* Warning */}
        {includeAdvance && advanceAmount > 0 && (
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="card border-warning-200 bg-warning-50 mb-8"
          >
            <div className="flex gap-3">
              <ExclamationTriangleIcon className="h-5 w-5 text-warning-600 flex-shrink-0" />
              <div className="text-sm text-warning-700">
                <p className="font-medium mb-1">Important Notes</p>
                <ul className="space-y-1">
                  <li>• You'll receive {formatCurrency(calculateNetProceeds())} (90% after 10% OID)</li>
                  <li>• Full {formatCurrency(advanceAmount)} drawn from ELOC</li>
                  <li>• Repayment at lowest 10-day VWAP via investor notices</li>
                  <li>• Registration statement required within 20 business days</li>
                </ul>
              </div>
            </div>
          </motion.div>
        )}

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4">
          <button
            onClick={handleProceed}
            className="btn-primary flex-1 flex items-center justify-center"
          >
            {includeAdvance ? 'Proceed with Advance' : 'Proceed without Advance'}
            <ArrowRightIcon className="h-5 w-5 ml-2" />
          </button>
          <button 
            onClick={() => navigate(-1)}
            className="btn-secondary flex-1"
          >
            Back to Deal Options
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdvanceDraw; 